type AnalyticsValue = string | number | boolean | null | undefined
type AnalyticsParams = Record<string, AnalyticsValue>
interface MetaEventOptions {
  eventID?: string
}
const attributionQueryKeys = new Set([
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
])

declare global {
  interface Window {
    googleAnalyticsMeasurementId?: string
    googleAdsConversionId?: string | null
    gtag?: (
      command: "config" | "event" | "js" | "set",
      target: string | Date,
      params?: AnalyticsParams,
    ) => void
    fbq?: (
      command: "init" | "track",
      target: string,
      params?: AnalyticsParams,
      options?: MetaEventOptions,
    ) => void
    umami?: {
      track: (event: string, data?: AnalyticsParams) => void
    }
    [gaDisable: `ga-disable-${string}`]: boolean | undefined
  }
}

let googleSilenced = false

export function silenceGoogleInsideApp(page: {
  component: string
  signedIn: boolean
  welcome: boolean
}) {
  googleSilenced =
    page.signedIn &&
    page.component !== "RegistrationProfile/Show" &&
    !page.welcome

  const measurementId = window.googleAnalyticsMeasurementId
  if (measurementId) window[`ga-disable-${measurementId}`] = googleSilenced
}

export function trackEvent(
  event: string,
  data?: Record<string, string | number | boolean>,
) {
  const eventName = normalizeAnalyticsName(event)
  const params = normalizeParams(data)

  gtag("event", eventName, params)
  window.umami?.track(eventName, params)
}

export function trackSignUp() {
  trackEvent("sign_up", { conversion_type: "signup" })
}

export function trackLead(
  eventId: string,
  data: Record<string, string | number | boolean>,
) {
  trackEvent("generate_lead", data)
  window.fbq?.("track", "Lead", normalizeParams(data), { eventID: eventId })
}

export function metaTrackingConsent() {
  return typeof window.fbq === "function" ? "1" : "0"
}

export function trackPageView(url: string, { signedIn = false } = {}) {
  if (window.googleAnalyticsMeasurementId) {
    const pageUrl = new URL(url, window.location.origin)
    const pagePath = `${pageUrl.pathname}${attributionSearch(pageUrl)}`

    gtag("config", window.googleAnalyticsMeasurementId, {
      page_location: `${pageUrl.origin}${pagePath}`,
      page_path: pagePath,
      page_title: document.title,
    })
  }

  // Meta bekommt nur öffentliche Seiten zu sehen. `_meta_pixel.html.erb`
  // spritzt den Pixel zwar nur für Ausgeloggte ein, aber die Anmeldung läuft
  // über eine Inertia-Navigation ohne Full Reload – `window.fbq` überlebt sie
  // und würde sonst ab da die URLs der Disposition melden.
  if (!signedIn) {
    window.fbq?.("track", "PageView")
  }
}

function gtag(...args: Parameters<NonNullable<Window["gtag"]>>) {
  if (!googleSilenced) window.gtag?.(...args)
}

function attributionSearch(url: URL) {
  const params = new URLSearchParams()

  url.searchParams.forEach((value, key) => {
    if (key.startsWith("utm_") || attributionQueryKeys.has(key)) {
      params.append(key, value)
    }
  })

  const search = params.toString()
  return search ? `?${search}` : ""
}

function normalizeParams(data?: Record<string, string | number | boolean>) {
  if (!data) return undefined

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      normalizeAnalyticsName(key),
      value,
    ]),
  )
}

function normalizeAnalyticsName(name: string) {
  return name.replace(/\W/gu, "_").slice(0, 40)
}
